import {
  DeleteReimbursablePayrollConceptDocument,
  DeleteReimbursablePayrollConceptMutationVariables,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

interface UseDeleteReimbursablePayrollConceptOptions {
  companyId: number
  reimbursableTypes: string[]
}

const useDeleteReimbursablePayrollConcept = (
  options: UseDeleteReimbursablePayrollConceptOptions
) => {
  const [deleteReimbursablePayrollConcept, { loading }] = useMutation(
    DeleteReimbursablePayrollConceptDocument,
    {
      extractorFn: (data) =>
        data.compensationsMutations.deleteCompensationsReimbursablePayrollConcept,
      update: (cache, { data }) => {
        const deletedConcept =
          data?.compensationsMutations.deleteCompensationsReimbursablePayrollConcept
            ?.reimbursablePayrollConcept

        if (!deletedConcept?.id) return

        if (deletedConcept.payrollConcept?.id) {
          const parentId = cache.identify({
            __typename: 'compensations',
            id: deletedConcept.payrollConcept.id,
          })
          if (!parentId) return

          cache.modify({
            id: parentId,
            fields: {
              reimbursablePayrollConceptsConnection(
                existingConnection,
                { readField, storeFieldName }
              ) {
                if (!existingConnection?.nodes) return existingConnection

                const expected = `reimbursablePayrollConceptsConnection({"companyId":${
                  options.companyId
                },"reimbursableTypes":${JSON.stringify([...options.reimbursableTypes].sort())}})`
                if (storeFieldName !== expected) return existingConnection

                const filteredNodes = existingConnection.nodes.filter((node: { __ref: string }) => {
                  const nodeId = readField('id', node)
                  return nodeId !== deletedConcept.id
                })

                const currentCount =
                  existingConnection.totalCount ?? existingConnection.nodes.length
                const nextCount = Math.max(
                  0,
                  currentCount - (existingConnection.nodes.length - filteredNodes.length)
                )

                return {
                  ...existingConnection,
                  nodes: filteredNodes,
                  totalCount: nextCount,
                }
              },
            },
          })
        }

        const entityId = cache.identify({
          __typename: 'CompensationsReimbursablePayrollConcept',
          id: deletedConcept.id,
        })
        if (!entityId) return
        cache.evict({ id: entityId })
        cache.gc()
      },
    }
  )

  return {
    loading,
    deleteReimbursablePayrollConcept: (
      variables: DeleteReimbursablePayrollConceptMutationVariables
    ) => deleteReimbursablePayrollConcept({ variables }),
  }
}

export default useDeleteReimbursablePayrollConcept
